import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { getTeamAvatarUrl, handleTeamAvatarError } from '../utils/avatars';
import './EventInviteModal.css';
import './EventTeamsSection.css';

// Ограничения загрузки — как у Api::UploadController#team_avatar
const AVATAR_MAX_SIZE = 5 * 1024 * 1024;
const AVATAR_TYPES = 'image/jpeg,image/png,image/gif,image/webp';

// Команда для события: новая команда под событие (POST /teams) или своя
// существующая — основная либо прежняя команда под событие. Для существующей
// следующим шагом выбирается состав на событие (onChooseTeam →
// EventTeamRosterModal). Аватар новой команды загружается при отправке формы,
// чтобы отмена не оставляла на сервере лишних файлов
// preferExisting — открыть сразу на выборе своей команды (кнопка в списке событий)
const EventTeamFormModal = ({ event, canCreate, availableTeams, preferExisting, onClose, onSaved, onChooseTeam }) => {
  const { t } = useLanguage();
  const [mode, setMode] = useState(
    !canCreate || (preferExisting && availableTeams.length > 0) ? 'existing' : 'new'
  );
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [teamId, setTeamId] = useState(availableTeams[0]?.id || null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    if (file.size > AVATAR_MAX_SIZE) {
      setError(t('profile.avatarTooLarge'));
      return;
    }

    setError('');
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const removeAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
  };

  const uploadAvatar = async () => {
    if (!avatarFile) return '';

    const body = new FormData();
    body.append('avatar', avatarFile);
    try {
      const response = await axios.post('/api/upload/team_avatar', body, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.path;
    } catch (err) {
      throw new Error(err.response?.data?.error || t('profile.avatarUploadError'));
    }
  };

  const canSubmit = mode === 'new' ? name.trim().length >= 3 : Boolean(teamId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    if (mode === 'existing') {
      onChooseTeam(availableTeams.find(team => team.id === teamId));
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const avatar = await uploadAvatar();
      await axios.post(`/api/events/${event.id}/teams`, {
        team: { name: name.trim(), description: description.trim(), avatar }
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message || t('common.error'));
      setSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="event-invite-overlay" onClick={handleOverlayClick}>
      <form className="event-invite-modal" onSubmit={handleSubmit}>
        <div className="event-invite-header">
          <div>
            <h2>{t('eventTeams.formTitle')}</h2>
            <div className="event-invite-subtitle">{event.name}</div>
          </div>
          <button type="button" className="event-invite-close" onClick={onClose}>×</button>
        </div>

        {canCreate && availableTeams.length > 0 && (
          <div className="event-team-form-tabs">
            <button
              type="button"
              className={`event-team-form-tab ${mode === 'new' ? 'active' : ''}`}
              onClick={() => setMode('new')}
            >
              {t('eventTeams.modeNew')}
            </button>
            <button
              type="button"
              className={`event-team-form-tab ${mode === 'existing' ? 'active' : ''}`}
              onClick={() => setMode('existing')}
            >
              {t('eventTeams.modeExisting')}
            </button>
          </div>
        )}

        <div className="event-invite-body event-team-form-body">
          {mode === 'new' ? (
            <>
              <div className="event-invite-hint">{t('eventTeams.newHint')}</div>
              <div className="event-team-form-avatar">
                <img
                  src={avatarPreview || getTeamAvatarUrl(null, name.trim() || 'T')}
                  alt=""
                  className="event-team-form-avatar-preview"
                  onError={(e) => handleTeamAvatarError(e, name.trim() || 'T')}
                />
                <div className="event-team-form-avatar-controls">
                  <label className="event-team-form-avatar-upload">
                    {avatarPreview ? t('teams.changeAvatar') : t('eventTeams.uploadAvatar')}
                    <input type="file" accept={AVATAR_TYPES} onChange={handleAvatarChange} hidden />
                  </label>
                  {avatarPreview && (
                    <button type="button" className="event-team-form-avatar-remove" onClick={removeAvatar}>
                      {t('teams.removeAvatar')}
                    </button>
                  )}
                </div>
              </div>
              <label className="event-team-form-field">
                <span>{t('eventTeams.name')}</span>
                <input
                  type="text"
                  className="event-invite-search"
                  value={name}
                  maxLength={100}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </label>
              <label className="event-team-form-field">
                <span>{t('eventTeams.description')}</span>
                <textarea
                  className="event-invite-search"
                  rows={3}
                  value={description}
                  maxLength={1000}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </label>
            </>
          ) : (
            <>
              <div className="event-invite-hint">{t('eventTeams.existingHint')}</div>
              {availableTeams.map(team => (
                <label key={team.id} className="event-invite-item">
                  <input
                    type="radio"
                    name="event-team"
                    checked={teamId === team.id}
                    onChange={() => setTeamId(team.id)}
                  />
                  <img
                    src={getTeamAvatarUrl(team.avatar, team.name)}
                    alt=""
                    className="event-invite-avatar"
                    onError={(e) => handleTeamAvatarError(e, team.name)}
                  />
                  <span className="event-invite-user">
                    <span className="event-invite-name">{team.name}</span>
                  </span>
                  {!team.event_team && (
                    <span className="event-team-kind">{t('eventTeams.mainTeam')}</span>
                  )}
                  {team.status === 'inactive' && (
                    <span className="event-team-status">{t('eventTeams.inactive')}</span>
                  )}
                </label>
              ))}
            </>
          )}
        </div>

        <div className="event-invite-footer">
          <div className="event-invite-status">
            {error && <span className="event-invite-error">{error}</span>}
          </div>
          <button type="submit" className="event-invite-submit" disabled={submitting || !canSubmit}>
            {submitting
              ? t('eventTeams.saving')
              : mode === 'new' ? t('eventTeams.submitCreate') : t('eventTeams.next')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EventTeamFormModal;
